/**
 * AnalyticsTabContent — Level, Streak, Listening, Words-Known cards.
 * Web port of client-native/src/features/profile/components/AnalyticsTabContent.tsx
 * plus the individual card components, consolidated for the web track.
 *
 * Level card consumes @/data/parrotLevels (SHEET_LEVELS) for the level name +
 * thermometer progress, threaded with the server-computed level + real progress
 * counts (level, reviewedCards, completedLessons) — the same stats the LearnPage
 * levels sheet trusts. learningWords / phrases come from useDictionaryStats (the
 * same source the dictionary header trusts), matching iOS CurrentLevelCard which
 * reads learningWordsCount / savedPhrasesCount off the dictionary-stats-fed
 * globalStore. totalWords is known + learning (not known only).
 */
import { FC, useState } from "react";
import { Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { colors, ThermometerProgressBar } from "@/ui";
import { useDictionaryStats } from "@/hooks/useDictionaryStats";
import { SHEET_LEVELS, type SheetLevel } from "@/data/parrotLevels";
import LevelsSheet from "@/components/Levels/LevelsSheet";
import StatCard from "./StatCard";
import { getLevelIconUrl, fireIcon, timerIcon, openBookIcon } from "./profileAssets";
import { formatCardListening } from "./profileFormat";

interface AnalyticsTabContentProps {
  streak: number;
  listeningTimeSeconds: number;
  wordsKnown: number;
  /** Server-computed level (drives the level card; same source as the levels sheet). */
  level: number;
  /** Real reviewed-cards count for the reviewedCards checkType. */
  reviewedCards: number;
  /** Real completed-lessons count for the completedLessons checkType. */
  completedLessons: number;
}

// ---------------------------------------------------------------------------
// Level progress (port of levelProgress.computeLevelProgress + formatProgressLabel)
// ---------------------------------------------------------------------------

interface ProgressStats {
  totalSeconds: number;
  totalWords: number;
  learningWords: number;
  reviewedCards: number;
  knownWords: number;
  completedLessons: number;
  phrases: number;
}

function currentForCheck(lvl: SheetLevel, s: ProgressStats): number {
  switch (lvl.checkType) {
    case "totalWords":
      return s.totalWords;
    case "learningWords":
      return s.learningWords;
    case "reviewedCards":
      return s.reviewedCards;
    case "knownWords":
      return s.knownWords;
    case "completedLessons":
      return s.completedLessons;
    case "phrases":
      return s.phrases;
    case "listeningTime":
      return s.totalSeconds;
  }
}

function computeLevelProgress(lvl: SheetLevel, s: ProgressStats): number {
  if (lvl.requirement === "Max level!") return 1;
  const current = currentForCheck(lvl, s);
  const span = lvl.goalValue - lvl.prevGoalValue;
  if (span <= 0) return 1;
  const into = current - lvl.prevGoalValue;
  return Math.max(0, Math.min(1, into / span));
}

function formatProgressLabel(lvl: SheetLevel, s: ProgressStats): string {
  if (lvl.requirement === "Max level!") return "Max";
  const cur = Math.min(currentForCheck(lvl, s), lvl.goalValue);
  switch (lvl.checkType) {
    case "totalWords":
    case "learningWords":
      return `${cur}/${lvl.goalValue} words`;
    case "reviewedCards":
      return `${cur}/${lvl.goalValue} cards`;
    case "knownWords":
      return `${cur}/${lvl.goalValue} mastered`;
    case "completedLessons":
      return `${cur}/${lvl.goalValue} lessons`;
    case "phrases":
      return `${cur}/${lvl.goalValue} phrases`;
    case "listeningTime": {
      const goalSec = lvl.goalValue;
      if (goalSec <= 3600) {
        return `${Math.floor(cur / 60)}/${Math.floor(goalSec / 60)}m`;
      }
      const goalH = Math.floor(goalSec / 3600);
      const goalM = Math.floor((goalSec % 3600) / 60);
      const curH = Math.floor(cur / 3600);
      const curM = Math.floor((cur % 3600) / 60);
      const goalLabel = goalM === 0 ? `${goalH}h` : `${goalH}h ${goalM}m`;
      if (curM === 0) {
        return `${curH}h / ${goalLabel}`;
      }
      return `${curH}h ${curM}m / ${goalLabel}`;
    }
  }
}

const CurrentLevelCard: FC<{
  listeningTimeSeconds: number;
  wordsKnown: number;
  level: number;
  reviewedCards: number;
  completedLessons: number;
}> = ({
  listeningTimeSeconds,
  wordsKnown,
  level,
  reviewedCards,
  completedLessons,
}) => {
  const [sheetVisible, setSheetVisible] = useState(false);
  // iOS CurrentLevelCard threads learningWords (learningWordsCount) and phrases
  // (savedPhrasesCount = known + learning phrases) into the level progress so
  // checkTypes like "Highlight 11 phrases" / learning-word goals show real
  // progress. useDictionaryStats is the same source the dictionary header uses.
  const { learningWordCount, totalPhraseCount } = useDictionaryStats();
  const displayLevel = level;
  const lvl = SHEET_LEVELS.find((l) => l.level === displayLevel);
  if (!lvl) return null;

  const stats: ProgressStats = {
    totalSeconds: listeningTimeSeconds,
    // iOS totalWords = knownWords + learningWords (CurrentLevelCard:44), not
    // known-only. wordsKnown here is the known count.
    totalWords: wordsKnown + learningWordCount,
    learningWords: learningWordCount,
    reviewedCards,
    knownWords: wordsKnown,
    completedLessons,
    phrases: totalPhraseCount,
  };

  const progress = computeLevelProgress(lvl, stats);
  const progressLabel = formatProgressLabel(lvl, stats);
  const icon = getLevelIconUrl(displayLevel);

  return (
    <>
      {/* iOS wraps the card in a Pressable that opens ParrotLevelsSheet. */}
      <button
        type="button"
        onClick={() => setSheetVisible(true)}
        style={{
          display: "block",
          width: "100%",
          textAlign: "left",
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
      >
        <StatCard
          value={`Level ${displayLevel}`}
          label={lvl.name}
          icon={icon ?? ""}
          backgroundColor="rgba(135, 206, 250, 0.65)"
          hideContentDivider
          iconSize={54}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1 }}>
              {/* iOS CurrentLevelCard.tsx:97-102 — no mount animation, white
                  translucent track/stripes. */}
              <ThermometerProgressBar
                progress={progress}
                shouldAnimate={false}
                trackColor="rgba(255, 255, 255, 0.4)"
                stripeColor="rgba(255, 255, 255, 0.28)"
              />
            </div>
            <span
              className="text-xs font-semibold"
              style={{ color: colors.primary, letterSpacing: 0.3, whiteSpace: "nowrap" }}
            >
              {progressLabel}
            </span>
          </div>
        </StatCard>
      </button>
      <LevelsSheet
        open={sheetVisible}
        currentLevel={displayLevel}
        progressStats={stats}
        onClose={() => setSheetVisible(false)}
      />
    </>
  );
};

const DAYS_OF_WEEK = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const StreakStatCard: FC<{ streak: number }> = ({ streak }) => {
  const currentDay = new Date().getDay();
  const daysIntoCurrentWeek = ((streak - 1) % 7) + 1;
  const startOffset = daysIntoCurrentWeek - 1;

  const dayLabels: string[] = [];
  for (let i = 0; i < 7; i++) {
    const dayIndex = (currentDay - startOffset + i + 7) % 7;
    dayLabels.push(DAYS_OF_WEEK[dayIndex]);
  }
  const streakLabel = streak === 1 ? "day streak" : "days streak";

  return (
    <StatCard
      value={streak.toString()}
      label={streakLabel}
      icon={fireIcon}
      backgroundColor="rgba(255, 180, 100, 0.65)"
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {dayLabels.map((day, index) => {
          const isStreakDay = index < daysIntoCurrentWeek;
          return (
            <div
              key={`${day}-${index}`}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isStreakDay
                    ? colors.orange
                    : "rgba(255,255,255,0.5)",
                }}
              >
                {isStreakDay && <Check size={14} color="white" strokeWidth={3} />}
              </div>
              <span
                className="font-semibold"
                style={{ color: colors.primary, fontSize: 12 }}
              >
                {day}
              </span>
            </div>
          );
        })}
      </div>
    </StatCard>
  );
};

const AnalyticsTabContent: FC<AnalyticsTabContentProps> = ({
  streak,
  listeningTimeSeconds,
  wordsKnown,
  level,
  reviewedCards,
  completedLessons,
}) => {
  const nav = useNavigate();

  return (
    <div style={{ paddingTop: 16, display: "flex", flexDirection: "column", gap: 16 }}>
      <CurrentLevelCard
        listeningTimeSeconds={listeningTimeSeconds}
        wordsKnown={wordsKnown}
        level={level}
        reviewedCards={reviewedCards}
        completedLessons={completedLessons}
      />
      <StreakStatCard streak={streak} />
      <StatCard
        value={formatCardListening(listeningTimeSeconds)}
        label="Total Listen Time"
        icon={timerIcon}
        backgroundColor="rgba(200, 191, 217, 0.85)"
        actionText="Keep watching"
        onPress={() => nav("/home/feed")}
      />
      <StatCard
        value={wordsKnown.toLocaleString()}
        label="Words Known"
        icon={openBookIcon}
        backgroundColor="rgba(144, 238, 144, 0.65)"
        actionText="View dictionary"
        onPress={() => nav("/home/dictionary")}
      />
    </div>
  );
};

export default AnalyticsTabContent;
